import type {
  ClaireFixReport,
  ClaireHandoffWindow,
  ClaireLiveQualityDay,
  ClaireLiveQualityReport,
  ClaireLiveQualityWindow,
  ClaireRegressionRun,
} from '@borradh-workspace/api-client/types';

/** "12.4%", or "–" when nothing was judged. */
export function formatShare(share: number | null | undefined): string {
  if (share === null || share === undefined || Number.isNaN(share)) return '–';
  return `${(share * 100).toFixed(1)}%`;
}

/** "26 Oct" from a `2026-10-26` calendar day (no clock involved). */
export function formatDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1)).toLocaleDateString(
    'en-GB',
    { day: 'numeric', month: 'short', timeZone: 'UTC' }
  );
}

export type TargetStatus = 'met' | 'above' | 'unknown';

/** At or under the top of the target band counts as met. */
export function targetStatus(
  share: number | null,
  target: ClaireLiveQualityReport['target']
): TargetStatus {
  if (share === null) return 'unknown';
  return share <= target.high ? 'met' : 'above';
}

/**
 * "Down 3.1 pts on the week before" — a fall is good. Null when either week
 * has no judged replies.
 */
export function weekChange(
  last7: ClaireLiveQualityWindow,
  previous7: ClaireLiveQualityWindow
): { text: string; better: boolean | null } | null {
  if (last7.seriousShare === null || previous7.seriousShare === null)
    return null;
  const pts = (last7.seriousShare - previous7.seriousShare) * 100;
  if (Math.abs(pts) < 0.05)
    return { text: 'Same as the 7 days before', better: null };
  return {
    text: `${pts < 0 ? 'Down' : 'Up'} ${Math.abs(pts).toFixed(1)} pts on the 7 days before`,
    better: pts < 0,
  };
}

/** The days a live fix reached production, for the chart's markers. */
export function fixMarkers(
  fixes: readonly ClaireFixReport[],
  days: readonly ClaireLiveQualityDay[]
) {
  const shown = new Set(days.map((d) => d.day));
  const byDay = new Map<string, ClaireFixReport[]>();
  for (const fix of fixes) {
    if (fix.status !== 'live' || !fix.liveOn || !shown.has(fix.liveOn))
      continue;
    byDay.set(fix.liveOn, [...(byDay.get(fix.liveOn) ?? []), fix]);
  }
  return [...byDay].map(([day, list]) => ({
    day,
    label: list.map((f) => `#${f.pr}`).join(' '),
    measurement: list.every((f) => f.kind === 'measurement'),
  }));
}

/** The last `n` days, newest last, for the mistake-type table. */
export const lastDays = (days: readonly ClaireLiveQualityDay[], n: number) =>
  days.slice(-n);

/**
 * The fix's replay result for one mistake type: the newest regression run
 * named `pr-<N>-…`, before (the baseline) and after, on the items both
 * scored. Null when the PR has no scored run with that type.
 */
export function replayEffect(
  pr: number,
  score: string,
  runs: readonly ClaireRegressionRun[]
): {
  before: number;
  after: number;
  items: number;
  runName: string;
  url: string;
} | null {
  const run = runs.find(
    (r) =>
      r.name.startsWith(`pr-${pr}-`) &&
      r.failureTypes.types.some((t) => t.score === score && t.paired)
  );
  const type = run?.failureTypes.types.find((t) => t.score === score);
  if (!run || !type?.paired) return null;
  return {
    before: type.paired.baselineFailed,
    after: type.paired.runFailed,
    items: type.paired.items,
    runName: run.name,
    url: run.url,
  };
}

export const FIX_STATUS_LABELS: Record<ClaireFixReport['status'], string> = {
  open: 'Open',
  merged: 'Merged, not live',
  live: 'Live',
};

export type OutcomeKey = 'handled' | 'handedOff' | 'failing';

/** The three outcomes, in page order, with their colours and API filter. */
export const OUTCOMES: readonly {
  key: OutcomeKey;
  filter: 'handled' | 'handed_off' | 'failing';
  label: string;
  meaning: string;
  /** Tailwind classes: text, bar fill. */
  text: string;
  fill: string;
}[] = [
  {
    key: 'handled',
    filter: 'handled',
    label: 'Handled by Claire',
    meaning: 'She answered or moved the conversation forward.',
    text: 'text-green-600 dark:text-green-400',
    fill: 'bg-green-600 dark:bg-green-500',
  },
  {
    key: 'handedOff',
    filter: 'handed_off',
    label: 'Handed to staff',
    meaning: 'She passed the conversation to the clinic team.',
    text: 'text-slate-700 dark:text-slate-300',
    fill: 'bg-slate-400 dark:bg-slate-500',
  },
  {
    key: 'failing',
    filter: 'failing',
    label: 'Failing',
    meaning: 'She got stuck or got it wrong.',
    text: 'text-red-600 dark:text-red-400',
    fill: 'bg-red-600 dark:bg-red-500',
  },
];

/** "64%" (whole percent, for the big numbers), or "–". */
export const formatPct = (share: number | null | undefined) =>
  share === null || share === undefined || Number.isNaN(share)
    ? '–'
    : `${Math.round(share * 100)}%`;

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/**
 * The live judge's handoff split in words, for under "Handed to staff".
 * Percentages only from `minJudged` scored handoffs: below that one verdict
 * moves the split by ten points or more, so the counts stand alone.
 */
export function handoffSplitText(
  window: ClaireHandoffWindow,
  minJudged: number
): {
  /** The lead-in before the right / avoidable counts. */
  lead: string;
  right: string;
  avoidable: string;
  /** Why there are no percentages; null when there are. */
  caveat: string | null;
  missing: string;
} {
  const pct = (share: number | null) =>
    window.judged >= minJudged && share !== null
      ? ` (${formatPct(share)})`
      : '';
  return {
    lead:
      window.judged === 0
        ? 'Judged by the live judge: no handoffs yet.'
        : `Judged by the live judge: ${plural(window.judged, 'handoff', 'handoffs')},`,
    right: `${window.right} right${pct(window.rightShare)}`,
    avoidable: `${window.unneeded} avoidable${pct(window.unneededShare)}`,
    caveat:
      window.judged > 0 && window.judged < minJudged
        ? `Too few for percentages: fewer than ${minJudged} scored.`
        : null,
    missing: `${plural(window.missing, 'turn', 'turns')} should have been handed off and ${window.missing === 1 ? "wasn't" : "weren't"}`,
  };
}

/** Each day's three shares in percent (0 when nothing was labelled). */
export function outcomeBars(days: readonly ClaireLiveQualityDay[]) {
  return days.map((d) => {
    const t = d.outcomes.total;
    const pct = (n: number) => (t ? (n / t) * 100 : 0);
    return {
      day: d.day,
      label: formatDay(d.day),
      total: t,
      handled: pct(d.outcomes.handled),
      handedOff: pct(d.outcomes.handedOff),
      failing: pct(d.outcomes.failing),
    };
  });
}

/** Status words that never pretend to know: hand-kept status is "unknown". */
export function fixStatusText(fix: ClaireFixReport): string {
  if (fix.statusSource === 'config') return 'Status unknown';
  return FIX_STATUS_LABELS[fix.status];
}

/** The fix's effect in plain words, from its replay and live numbers. */
export function fixEffectText(
  fix: ClaireFixReport,
  report: ClaireLiveQualityReport,
  runs: readonly ClaireRegressionRun[] | undefined
): string {
  if (fix.targets.length === 0)
    return 'Not measured on this page: it does not change what Claire replies.';
  const parts = fix.effects.map((e) => {
    const score = report.types.find((t) => t.label === e.label)?.score;
    const replay = score && runs ? replayEffect(fix.pr, score, runs) : null;
    const name = e.label;
    if (e.before?.share != null && e.after?.share != null) {
      const down = e.after.share < e.before.share;
      return `${name} ${down ? 'down' : 'up'} from ${formatShare(e.before.share)} to ${formatShare(e.after.share)} of live replies`;
    }
    if (replay)
      return `${name} ${replay.before} → ${replay.after} of ${replay.items} in its test run`;
    return null;
  });
  const known = parts.filter(Boolean);
  if (known.length) return `${known.join('; ')}.`;
  return fix.status === 'live' && fix.statusSource === 'github'
    ? 'Too soon to tell: needs a full day live.'
    : 'Not measured yet.';
}
